import React, { useState, useEffect } from "react";
import SEO from "../components/SEO";
import { GraduationCap, Mail, Search, X, ArrowRight, Send, CreditCard, CheckCircle, Download, FileCheck, AlertCircle, RotateCw, ChevronDown } from "lucide-react";

export const COURSES_FAQS = [
  {
    q: "What is the best tech academy in Jigawa State and Northern Nigeria?",
    a: "InnovaTech Academy (InnovaTech Digital Institute), located at No.2 Salmaniyya Plaza, Opposite TCN, Ibrahim Aliyu Bypass, Dutse, Jigawa State, is the top-ranked tech academy in the state. We offer practical, project-based training in Full-Stack Web Development, Cybersecurity, AI Automation, and Cloud Engineering with 1-on-1 mentorship.",
  },
  {
    q: "Can complete beginners with zero coding or tech background enroll at InnovaTech?",
    a: "Yes, absolutely! Over 60% of our successful graduates started with zero programming knowledge. Our curriculum starts from computer fundamentals and logic building before advancing into real-world software engineering with step-by-step guidance.",
  },
  {
    q: "Are the classes physical in Dutse, Jigawa State, or available online across Nigeria?",
    a: "We offer both! You can attend physical in-person classes at our air-conditioned digital innovation hub in Dutse, Jigawa State (with high-speed fiber internet and uninterrupted backup power), or join live instructor-led virtual bootcamps from anywhere in Nigeria.",
  },
  {
    q: "How much do courses cost at InnovaTech Academy, and are installment payments allowed?",
    a: "Our courses range from accessible free community workshops to intensive professional bootcamps starting from ₦35,000. We offer flexible 2-stage installment payment options to ensure financial accessibility for students and young professionals across Nigeria.",
  },
  {
    q: "Can university and polytechnic students combine these courses with SIWES Industrial Training?",
    a: "Yes! We offer specialized dual-track enrollment where students fulfilling their 3-month or 6-month SIWES (Industrial Training) or NYSC service in Jigawa/Northern Nigeria work on live production software projects while mastering industry-grade tech stacks.",
  },
  {
    q: "What certifications will I receive after completing a course?",
    a: "Upon submitting and defending your capstone project, you receive an official, verifiable Certificate of Completion from InnovaTech Consultancy Limited with a unique verification ID that can be shared on LinkedIn and attached to your resume.",
  },
  {
    q: "What programming languages and tools will I learn?",
    a: "Depending on your track, you will master modern industry tools including HTML5/CSS3, JavaScript (ES6+), TypeScript, React, Node.js, Python for AI & Data, Linux CLI, Docker, Git/GitHub, and cloud deployment pipelines.",
  },
  {
    q: "Does InnovaTech help graduates secure internships and tech jobs?",
    a: "Yes! Outstanding graduates receive direct internship placements within InnovaTech's engineering department or partner tech companies across Nigeria. We also provide resume optimization, portfolio reviews, and technical interview coaching.",
  },
  {
    q: "What are the class schedules, and can working professionals or university students participate?",
    a: "We offer flexible weekday (morning and afternoon) and weekend cohorts designed specifically to accommodate university students, NYSC corps members, and working professionals without disrupting their daily routines.",
  },
  {
    q: "How do I apply or register for an upcoming cohort in Dutse or online?",
    a: "You can enroll directly on this website by clicking the 'Enroll Now' button on your selected course, visit our Dutse hub at Salmaniyya Plaza, or message our admissions desk on WhatsApp at +234 813 023 7608 for instant onboarding.",
  },
];

export interface Course {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  tags: string[];
  banner_image: string;
  color: string;
  show_banner?: boolean;
  category?: string;
  is_free?: boolean;
  discounted_price?: number;
}

const formatNaira = (amount: number) => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount);
};

let coursesCache: Course[] | null = null;
let coursesPromise: Promise<Course[]> | null = null;

export function fetchCoursesCached(): Promise<Course[]> {
  if (coursesCache) return Promise.resolve(coursesCache);
  if (!coursesPromise) {
    coursesPromise = fetch("/api/courses")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load courses");
        return res.json();
      })
      .then((data) => {
        coursesCache = data;
        return data;
      })
      .catch((err) => {
        coursesPromise = null;
        throw err;
      });
  }
  return coursesPromise;
}

export function clearCoursesCache(): void {
  coursesCache = null;
  coursesPromise = null;
}

const CoursesScreen: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [checkoutStep, setCheckoutStep] = useState<"idle" | "form" | "processing" | "success" | "verifying" | "error" | "applied">("idle");
  const [justification, setJustification] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentReference, setPaymentReference] = useState<string | null>(null);
  const [isVerificationRetryable, setIsVerificationRetryable] = useState(false);
  const [downloadState, setDownloadState] = useState<"idle" | "loading" | "started">("idle");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedType, setSelectedType] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const filteredCourses = courses.filter((course) => {
    const matchesCategory = selectedCategory === "All" || course.category === selectedCategory;
    const matchesType = selectedType === "All" ||
      (selectedType === "Paid" && !course.is_free) ||
      (selectedType === "Free" && course.is_free);
    const matchesSearch = searchQuery.trim() === "" ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (course.tags && course.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
    return matchesCategory && matchesType && matchesSearch;
  });

  const handleDownloadReceipt = async () => {
    if (!paymentReference || downloadState !== "idle") return;

    setDownloadState("loading");

    try {
      // 1. Fetch the receipt HTML content from the API
      const response = await fetch(`/api/payments/receipt?reference=${encodeURIComponent(paymentReference)}&raw=true`);
      if (!response.ok) {
        throw new Error("Failed to fetch receipt content");
      }
      let html = await response.text();

      // 2. Ensure html2pdf is loaded in the parent window
      if (!(window as any).html2pdf) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Failed to load html2pdf script"));
          document.head.appendChild(script);
        });
      }

      // 3. Create a hidden iframe to render the receipt
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "800px";
      iframe.style.height = "1130px";
      iframe.style.left = "-9999px";
      iframe.style.top = "-9999px";
      iframe.style.border = "none";
      iframe.style.display = "block"; // Visible layout off-screen so stylesheets are parsed/applied
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error("Could not access iframe document");
      }

      // 4. Strip scripts and loading screens to avoid side effects
      html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
      html = html.replace(/<div id="loader"[\s\S]*?<\/div>/i, "");

      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      // 5. Wait for images (logo, digital stamp, watermark) to fully render inside the iframe
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 1000);
      });

      const receiptCard = iframeDoc.querySelector(".receipt-card");
      if (!receiptCard) {
        throw new Error("Receipt element not found in template");
      }

      // 6. Copy iframe's style tags inside the receipt card so html2canvas captures the styles correctly
      const styleTags = iframeDoc.querySelectorAll("style");
      styleTags.forEach((style) => {
        receiptCard.appendChild(style.cloneNode(true));
      });

      // 7. Generate the PDF from parent context using parent's html2pdf instance
      const opt = {
        margin: [12, 12, 12, 12],
        filename: `InnovaTech-Receipt-${paymentReference}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2.5,
          useCORS: true,
          allowTaint: true,
          logging: false,
          letterRendering: true,
          imageTimeout: 5000,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      await (window as any).html2pdf().set(opt).from(receiptCard).save();

      // 8. Cleanup the iframe
      document.body.removeChild(iframe);

      // 9. Set state to started, then reset to idle
      setDownloadState("started");
      setTimeout(() => {
        setDownloadState("idle");
      }, 3000);

    } catch (err) {
      console.error("Error generating receipt PDF:", err);
      alert("Failed to generate PDF receipt. Opening receipt page in a new window instead.");
      window.open(`/api/payments/receipt?reference=${encodeURIComponent(paymentReference)}&raw=true`, "_blank");
      setDownloadState("idle");
    }
  };


  const verifyPayment = async (ref: string, currentCourses: Course[]) => {
    setCheckoutStep("verifying");
    setErrorMessage("");
    setPaymentReference(ref);
    setIsVerificationRetryable(false);

    try {
      const verifyRes = await fetch(`/api/payments/verify?reference=${encodeURIComponent(ref)}`);
      const verifyData = await verifyRes.json();
      if (verifyRes.ok && verifyData.success) {
        const matchingCourse = currentCourses.find((c) => c.id === verifyData.courseId);
        if (matchingCourse) {
          setSelectedCourse(matchingCourse);
        }
        if (verifyData.email) {
          setEmail(verifyData.email);
        }
        // Show success immediately — don't block UI on confetti loading
        setCheckoutStep("success");
        // Fire optimized confetti on a dedicated canvas worker so it never
        // blocks the main thread. Dynamic import means it's only loaded on success.
        try {
          const { default: confettiLib } = await import("canvas-confetti");
          const canvas = document.createElement("canvas");
          canvas.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;width:100%;height:100%";
          document.body.appendChild(canvas);
          const myConfetti = confettiLib.create(canvas, { resize: true, useWorker: true });
          myConfetti({
            particleCount: 80,
            spread: 70,
            origin: { y: 0.6 },
            ticks: 180,
          });
          // Auto-remove canvas after the animation finishes (~3s)
          setTimeout(() => { canvas.remove(); }, 3500);
        } catch (cErr) {}
      } else {
        const status = verifyData.status;
        const isDeclined = status === "failed" || status === "declined" || status === "abandoned";
        setErrorMessage(verifyData.error || verifyData.message || "Payment verification failed. Please check with your bank.");
        setCheckoutStep("error");
        setIsVerificationRetryable(!isDeclined);
      }
    } catch (verifyErr: any) {
      console.error("Failed to verify payment:", verifyErr);
      setErrorMessage("A network error occurred while verifying payment status. Please check your internet connection.");
      setCheckoutStep("error");
      setIsVerificationRetryable(true);
    }
  };

  const closeModal = () => {
    setCheckoutStep("idle");
    setSelectedCourse(null);
    setPaymentReference(null);
    setIsVerificationRetryable(false);
    setErrorMessage("");
  };

  useEffect(() => {
    const fetchCourses = async () => {
      let loadedCourses: Course[] = [];
      try {
        loadedCourses = await fetchCoursesCached();
        setCourses(loadedCourses);
      } catch (err) {
        console.error("Failed to fetch courses, using fallback", err);
      } finally {
        setLoading(false);
      }

      // Check for reference parameter to verify payment
      const searchParams = new URLSearchParams(window.location.search);
      const reference = searchParams.get("reference");
      if (reference) {
        await verifyPayment(reference, loadedCourses);
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {}
      }
    };
    fetchCourses();
  }, []);

  const handleEnrollClick = (course: Course) => {
    setSelectedCourse(course);
    setCheckoutStep("form");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setJustification("");
    setErrorMessage("");
  };

  const handleCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) {
      setErrorMessage("Please enter your first name.");
      return;
    }
    if (!lastName.trim()) {
      setErrorMessage("Please enter your last name.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setErrorMessage("Please enter a valid phone number (WhatsApp recommended).");
      return;
    }

    if (!selectedCourse) return;

    if (selectedCourse.is_free && !justification.trim()) {
      setErrorMessage("Please state why you want to enroll in this course.");
      return;
    }

    setCheckoutStep("processing");
    setErrorMessage("");

    try {
      const initRes = await fetch("/api/payments/initialize", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          courseId: selectedCourse.id,
          justification: selectedCourse.is_free ? justification.trim() : undefined,
        }),
      });

      const initData = await initRes.json();

      if (initRes.ok) {
        if (initData.is_free) {
          setPaymentReference(initData.reference);
          setCheckoutStep("applied");
        } else if (initData.authorization_url) {
          window.location.href = initData.authorization_url;
        } else {
          setErrorMessage(initData.error || "Payment initialization failed. Please try again.");
          setCheckoutStep("form");
        }
      } else {
        setErrorMessage(initData.error || "Payment initialization failed. Please try again.");
        setCheckoutStep("form");
      }
    } catch (err: any) {
      console.error("Failed to initialize payment:", err);
      setErrorMessage("Network error: Could not contact payment server.");
      setCheckoutStep("form");
    }
  };

  return (
    <div className="bg-canvas dark:bg-[#0C1B2E] min-h-screen relative">
      <SEO
        title="Academy | InnovaTech"
        description="Enroll in industry-certified tech courses in Nigeria and Jigawa State. Master Full-Stack Web Development, Cybersecurity, AI Automation, and Cloud Engineering at InnovaTech Academy."
        keywords="best tech academy, best tech academy in jigawa state, computer training school dutse, learn coding dutse jigawa, tech bootcamps nigeria, practical tech training dutse, it courses nigeria, innovatech digital institute, software engineering course jigawa"
        canonical="/courses"
      >
        {/* EducationalOrganization schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: "InnovaTech Digital Institute & Tech Academy",
            alternateName: "InnovaTech Academy",
            url: "https://www.innovatech-ng.com/courses",
            logo: "https://www.innovatech-ng.com/logo/logo.png",
            description:
              "The premier technology academy and coding bootcamp in Dutse, Jigawa State and Northern Nigeria. Practical project-based training in software development, cybersecurity, AI, and IT infrastructure.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No.2 Salmaniyya Plaza, Opp. TCN, Ibrahim Aliyu Bypass",
              addressLocality: "Dutse",
              addressRegion: "Jigawa",
              postalCode: "720101",
              addressCountry: "NG",
            },
            telephone: "+234 813 023 7608",
            priceRange: "₦₦",
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "InnovaTech Academy Tech Courses",
              itemListElement: [
                {
                  "@type": "Course",
                  name: "Full-Stack Web Development & Software Engineering",
                  description:
                    "Comprehensive fullstack software development bootcamp covering React, Node.js, TypeScript, PostgreSQL, and cloud deployments.",
                  provider: {
                    "@type": "Organization",
                    name: "InnovaTech Academy",
                  },
                },
                {
                  "@type": "Course",
                  name: "Cybersecurity & Network Defense",
                  description:
                    "Practical cybersecurity training covering ethical hacking, network security, penetration testing, and security auditing.",
                  provider: {
                    "@type": "Organization",
                    name: "InnovaTech Academy",
                  },
                },
                {
                  "@type": "Course",
                  name: "Artificial Intelligence & Automation",
                  description:
                    "Hands-on AI workflows, machine learning concepts, LLM prompt engineering, and intelligent workflow automation.",
                  provider: {
                    "@type": "Organization",
                    name: "InnovaTech Academy",
                  },
                },
              ],
            },
          })}
        </script>

        {/* FAQPage Schema for Tech Academy Search Intent */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: COURSES_FAQS.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a,
              },
            })),
          })}
        </script>
      </SEO>

      {/* Grid background matching home/team */}
      <div className="absolute inset-0 technical-grid-bg opacity-[0.15] dark:opacity-[0.05] pointer-events-none z-0"></div>

      {/* Header section */}
      <section className="relative pt-16 pb-8 px-4 sm:px-8 z-10">
        <div className="mx-auto max-w-[1024px] flex flex-col gap-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
              01 · TECH ACADEMY
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-[-0.035em] text-text-main dark:text-white leading-[1.0]">
            Build In-Demand <br />
            <span className="inline-flex items-center gap-3 sm:gap-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-600">
                Tech Skills.
              </span>
              <img 
                src="/innova-academy-logo.png" 
                alt="InnovaTech Academy Logo" 
                className="h-10 w-auto sm:h-16 object-contain align-middle"
              />
            </span>
          </h1>
          <p className="max-w-xl text-lg font-light leading-relaxed text-text-sub dark:text-gray-400">
            Choose a practical tech course designed for beginners and professionals. Learn by doing, build a portfolio, and earn a recognized certificate.
          </p>
        </div>
      </section>

      {/* Course List / Grid */}
      <section className="py-12 px-4 sm:px-8 relative z-10">
        <div className="mx-auto max-w-[1024px]">
          {loading ? (
            // Skeleton Loader
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white dark:bg-[#152335] rounded-2xl h-[300px] border border-border-subtle dark:border-white/5 p-8 flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="h-6 w-3/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 w-5/6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="h-5 w-1/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-10 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="py-16 px-8 rounded-2xl bg-white dark:bg-[#0E1F35] border border-border-subtle dark:border-white/10 text-center flex flex-col items-center justify-center gap-4 max-w-lg mx-auto shadow-sm animate-in fade-in duration-500">
              <div className="size-16 rounded-2xl bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-red-400/30 flex items-center justify-center text-primary dark:text-red-400">
                <GraduationCap className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <h3 className="font-display text-xl font-bold text-text-main dark:text-white">No Courses Available</h3>
                <p className="text-sm text-text-sub dark:text-gray-400 leading-relaxed max-w-[340px] mx-auto">
                  We are currently updating our digital curriculum. Please check back later or contact us to ask about our upcoming classes.
                </p>
              </div>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-red-600 text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 cursor-pointer mt-2"
              >
                <span>Contact Admissions</span>
                <Mail className="w-[14px] h-[14px]" />
              </a>
            </div>
          ) : (
            <div className="space-y-10">
              {/* Search Bar */}
              <div className="max-w-md mx-auto relative group">
                <div className="relative flex items-center bg-white/50 dark:bg-white/5 border border-black/5 dark:border-white/5 rounded-2xl shadow-sm backdrop-blur-md transition-all duration-300 focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/20">
                  <Search className="absolute left-4 text-text-sub dark:text-gray-400 w-5 h-5 select-none group-focus-within:text-primary transition-colors" />
                  <input
                    type="text"
                    placeholder="Search courses by name or skill..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent pl-12 pr-10 py-3.5 text-sm text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-500 outline-none rounded-2xl"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-4 text-text-sub dark:text-gray-400 hover:text-primary dark:hover:text-red-400 transition-colors cursor-pointer flex items-center justify-center"
                      title="Clear search"
                      aria-label="Clear search"
                    >
                      <X className="w-[18px] h-[18px]" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filters Block */}
              <div className="flex flex-col gap-4 items-center">
                {/* Category Selector Tab Menu */}
                <div className="flex flex-wrap justify-center gap-1.5 p-1.5 rounded-2xl md:rounded-full bg-white/50 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-sm backdrop-blur-md">
                  {["All", "Beginner/Foundational", "Intermediate", "Advanced"].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl md:rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 cursor-pointer ${
                        selectedCategory === cat
                          ? "bg-white dark:bg-[#162A45] text-primary shadow-sm border border-black/[0.03] dark:border-white/5"
                          : "text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white"
                      }`}
                    >
                      {cat === "All" ? "All Levels" : cat === "Beginner/Foundational" ? "Beginner" : cat}
                    </button>
                  ))}
                </div>

                {/* Course Type Access Selector Tab Menu */}
                <div className="flex flex-wrap justify-center gap-1.5 p-1.5 rounded-2xl md:rounded-full bg-white/50 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-sm backdrop-blur-md">
                  {["All", "Paid", "Free"].map((type) => (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl md:rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 cursor-pointer ${
                        selectedType === type
                          ? "bg-white dark:bg-[#162A45] text-primary shadow-sm border border-black/[0.03] dark:border-white/5"
                          : "text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white"
                      }`}
                    >
                      {type === "All" ? "All Access" : type === "Paid" ? "Paid Courses" : "Free"}
                    </button>
                  ))}
                </div>
              </div>

              {filteredCourses.length === 0 ? (
                <div className="py-16 text-center text-sm text-text-sub dark:text-gray-400">
                  No courses found in this category.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredCourses.map((course) => (
                    <div
                      key={course.id}
                      className="group relative overflow-hidden rounded-2xl bg-white dark:bg-[#0E1F35] border border-border-subtle dark:border-white/10 hover:border-primary/40 dark:hover:border-primary/40 transition-[border-color,box-shadow,transform] duration-300 shadow-md hover:shadow-xl flex flex-col justify-between min-h-[380px]"
                    >
                      {/* Decorative background radial glow - optimized using hardware-accelerated radial-gradient */}
                      <div
                        className="absolute bottom-0 right-0 w-48 h-48 pointer-events-none z-0"
                        style={{
                          background: `radial-gradient(circle at bottom right, ${
                            course.color.includes("blue") ? "rgba(59, 130, 246, 0.12)" :
                            course.color.includes("emerald") ? "rgba(16, 185, 129, 0.12)" :
                            course.color.includes("red") ? "rgba(239, 68, 68, 0.12)" :
                            course.color.includes("purple") ? "rgba(139, 92, 246, 0.12)" :
                            course.color.includes("amber") ? "rgba(245, 158, 11, 0.12)" :
                            course.color.includes("orange") ? "rgba(249, 115, 22, 0.12)" :
                            course.color.includes("cyan") ? "rgba(6, 182, 212, 0.12)" :
                            course.color.includes("indigo") ? "rgba(99, 102, 241, 0.12)" :
                            course.color.includes("violet") ? "rgba(139, 92, 246, 0.12)" :
                            "rgba(238, 44, 40, 0.12)"
                          }, transparent 70%)`
                        }}
                      />

                      {/* Banner Image (if visible) */}
                      {course.show_banner !== false && course.banner_image && (
                        <div className="relative h-44 w-full overflow-hidden border-b border-black/[0.05] dark:border-white/5 shrink-0">
                          <img
                            src={course.banner_image}
                            alt={`${course.title} banner`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
                          {course.is_free && (
                            <span className="absolute top-4 left-4 z-10 rounded-full bg-primary text-white text-[9px] font-extrabold uppercase tracking-widest px-3 py-1 shadow-md border border-white/10">
                              Limited Offer
                            </span>
                          )}
                        </div>
                      )}

                      <div className="p-8 flex flex-col justify-between flex-grow">
                        <div>
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex flex-wrap gap-1.5 max-w-[70%]">
                              {course.is_free && course.show_banner === false && (
                                <span className="rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-red-400 px-2.5 py-0.5 text-[9px] font-extrabold uppercase tracking-widest border border-primary/15 dark:border-red-400/25">
                                  Limited Offer
                                </span>
                              )}
                              {course.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded-full bg-black/[0.04] dark:bg-white/5 px-2.5 py-0.5 text-[10px] font-mono font-medium text-text-sub dark:text-gray-400 border border-black/[0.03] dark:border-white/5"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <span className="text-xs font-mono font-semibold tracking-wider text-primary dark:text-red-400 bg-primary/5 dark:bg-primary/10 px-2.5 py-1 rounded-md border border-primary/10 dark:border-red-400/20">
                              {course.duration}
                            </span>
                          </div>

                          <h3 className="font-display text-2xl font-bold text-text-main dark:text-white mb-3 group-hover:text-primary transition-colors duration-300">
                            {course.title}
                          </h3>
                          <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed mb-6 font-light">
                            {course.description}
                          </p>
                        </div>

                        <div className="flex items-center justify-between mt-auto pt-6 border-t border-black/[0.05] dark:border-white/5">
                          <div>
                            <span className="text-[10px] font-mono tracking-widest text-text-sub dark:text-gray-500 uppercase block">
                              Tuition Fee
                            </span>
                            {course.is_free ? (
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-xl font-bold text-primary dark:text-red-400">
                                  FREE
                                </span>
                                <span className="text-xs line-through text-text-sub dark:text-gray-500 font-normal">
                                  {formatNaira(course.discounted_price || 0)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xl font-bold text-text-main dark:text-white">
                                {formatNaira(course.price)}
                              </span>
                            )}
                          </div>

                          <button
                            onClick={() => handleEnrollClick(course)}
                            className="group/btn inline-flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-xl bg-primary hover:bg-red-600 text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md hover:shadow-lg active:scale-95 cursor-pointer"
                          >
                            <span>Enroll Now</span>
                            <span className="size-6 rounded-lg bg-white/15 flex items-center justify-center transition-transform group-hover/btn:translate-x-0.5">
                              <ArrowRight className="w-[14px] h-[14px]" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Minimalist Overview Highlights & FAQ Section */}
          <div className="mt-28 md:mt-36 flex flex-col gap-24 border-t border-black/[0.06] dark:border-white/10 pt-16">
            {/* Section Header */}
            <div className="flex flex-col gap-5 text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                  02 · CURRICULUM EXCELLENCE
                </span>
              </div>
              
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.035em] text-text-main dark:text-white leading-[1.05]">
                Why InnovaTech Leads in <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">
                  Technical Education.
                </span>
              </h2>
              <p className="text-sm sm:text-base text-text-sub dark:text-gray-400 font-light leading-relaxed max-w-2xl">
                We combine industry-standard curriculum, physical computer labs in Dutse, and certified instructor guidance to turn beginners into job-ready software developers and cybersecurity analysts.
              </p>

              {/* Three Cards Matching Site's Nested Glass Card Language */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                  <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs font-bold tracking-widest text-primary">01</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                        Practical
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                        Hands-on Labs
                      </h3>
                      <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                        Over 80% of class time is spent building production software, configuring networks, and solving real security challenges.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                  <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs font-bold tracking-widest text-primary">02</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                        Accredited
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                        Verifiable Certificates
                      </h3>
                      <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                        Earn accredited, globally recognized certificates that employers, universities, and international organizations verify.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                  <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs font-bold tracking-widest text-primary">03</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                        Flexible
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                        Hybrid &amp; Physical Hub
                      </h3>
                      <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                        Attend classes physically at our air-conditioned Dutse hub or join interactive live virtual classes anywhere across Nigeria.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Minimalist FAQ Accordion Section */}
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                    03 · QUESTIONS
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-[-0.03em] text-text-main dark:text-white leading-[1.1]">
                  Frequently Asked <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">
                    Questions.
                  </span>
                </h3>
              </div>

              <div className="flex flex-col gap-4">
                {COURSES_FAQS.map((faq, idx) => (
                  <div
                    key={idx}
                    className="p-1 rounded-[1.75rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft transition-all duration-300"
                  >
                    <div className="rounded-[1.5rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full p-6 sm:p-7 text-left flex items-center justify-between gap-4 font-semibold text-sm sm:text-base text-text-main dark:text-white cursor-pointer hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors"
                      >
                        <span className="font-display tracking-tight">{faq.q}</span>
                        <span className="font-mono text-xs font-bold text-primary shrink-0 ml-2">
                          {openFaq === idx ? "−" : "+"}
                        </span>
                      </button>

                      {/* Smooth Animated Collapsible Content using CSS Grid */}
                      <div
                        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                          openFaq === idx ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-6 pb-6 sm:px-7 sm:pb-7 pt-1 text-xs sm:text-sm text-text-sub dark:text-gray-300 font-light leading-relaxed border-t border-black/[0.04] dark:border-white/5">
                            {faq.a}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout Modal (Paystack Simulated Checkout) */}
      {checkoutStep !== "idle" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="w-full max-w-md bg-white dark:bg-[#122238] rounded-2xl border border-border-subtle dark:border-white/10 shadow-2xl p-6 relative overflow-hidden animate-in zoom-in-95 duration-300">
            
            {/* Modal header */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-text-main dark:text-white">
                {selectedCourse?.is_free ? "Course Application" : "Course Enrollment"}
              </h3>
              <button
                onClick={closeModal}
                className="size-8 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 flex items-center justify-center text-text-sub dark:text-gray-400 transition-colors"
              >
                <X className="w-[18px] h-[18px]" />
              </button>
            </div>

            {checkoutStep === "form" && selectedCourse && (
              <form onSubmit={handleCheckoutSubmit} className="space-y-4">
                <div className="p-4 rounded-xl bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/5 mb-4">
                  <span className="text-[10px] font-mono tracking-widest text-text-sub dark:text-gray-500 uppercase">SELECTED COURSE</span>
                  <h4 className="font-bold text-text-main dark:text-white mt-1 text-base">{selectedCourse.title}</h4>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-black/[0.05] dark:border-white/5">
                    <span className="text-xs text-text-sub dark:text-gray-400">Tuition Price:</span>
                    {selectedCourse.is_free ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-primary dark:text-red-400">LIMITED OFFER</span>
                        <span className="text-xs line-through text-text-sub dark:text-gray-500">{formatNaira(selectedCourse.discounted_price || 0)}</span>
                      </div>
                    ) : (
                      <span className="font-bold text-primary dark:text-red-400">{formatNaira(selectedCourse.price)}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label htmlFor="firstName" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Amina"
                      className="w-full text-sm bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-600 outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="lastName" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Bello"
                      className="w-full text-sm bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-600 outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="email" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full text-sm bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-600 outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="phone" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                    Phone Number <span className="font-normal text-text-sub dark:text-gray-500">(WhatsApp recommended)</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 11) {
                        setPhone(val);
                      }
                    }}
                    placeholder="08012345678"
                    className="w-full text-sm bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-600 outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                {selectedCourse.is_free && (
                  <div className="space-y-1.5">
                    <label htmlFor="justification" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                      Statement of Purpose *
                    </label>
                    <textarea
                      id="justification"
                      required
                      rows={3}
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                      placeholder="Please explain why you want to enroll in this course and what you hope to achieve."
                      className="w-full text-sm bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-600 outline-none focus:ring-2 focus:ring-primary/40"
                    ></textarea>
                  </div>
                )}

                {errorMessage && (
                  <p className="text-xs text-red-500 font-medium">{errorMessage}</p>
                )}

                <button
                  type="submit"
                  className="w-full mt-4 bg-primary hover:bg-red-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-primary/20 active:scale-98 transition-all text-sm uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  {selectedCourse.is_free ? (
                    <Send className="w-[18px] h-[18px]" />
                  ) : (
                    <CreditCard className="w-[18px] h-[18px]" />
                  )}
                  <span>{selectedCourse.is_free ? "Submit Application" : "Pay with Paystack"}</span>
                </button>
              </form>
            )}

            {checkoutStep === "processing" && (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-primary/25 border-t-primary animate-spin"></div>
                <h4 className="font-bold text-text-main dark:text-white mt-2">Connecting to Payment Provider...</h4>
                <p className="text-xs text-text-sub dark:text-gray-400 max-w-[280px]">
                  Please do not close this window. We are opening the payment form.
                </p>
              </div>
            )}

            {checkoutStep === "verifying" && (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-primary/25 border-t-primary animate-spin"></div>
                <h4 className="font-bold text-text-main dark:text-white mt-2">Verifying Payment...</h4>
                <p className="text-xs text-text-sub dark:text-gray-400 max-w-[280px]">
                  Confirming your transaction status with Paystack. Please do not refresh.
                </p>
              </div>
            )}

            {checkoutStep === "success" && (
              <div className="flex flex-col items-center justify-center py-8 text-center gap-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
                  <CheckCircle className="w-9 h-9" />
                </div>
                <h4 className="text-xl font-bold text-text-main dark:text-white">Payment Successful!</h4>
                <p className="text-xs text-text-sub dark:text-gray-400 max-w-[300px] leading-relaxed">
                  Your payment has been verified successfully. A confirmation email has been sent to <strong className="text-text-main dark:text-white">{email}</strong> shortly with your course credentials.
                </p>
                
                <button
                  onClick={handleDownloadReceipt}
                  disabled={downloadState !== "idle"}
                  className={`w-full mt-6 text-white font-bold py-3.5 rounded-xl transition-all shadow-md active:scale-98 text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer ${
                    downloadState === "loading"
                      ? "bg-primary/70 cursor-not-allowed"
                      : downloadState === "started"
                      ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20"
                      : "bg-primary hover:bg-red-600 shadow-primary/20"
                  }`}
                >
                  {downloadState === "loading" && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {downloadState === "idle" && (
                    <Download className="w-[18px] h-[18px]" />
                  )}
                  {downloadState === "started" && (
                    <CheckCircle className="w-[18px] h-[18px]" />
                  )}
                  <span>
                    {downloadState === "loading"
                      ? "Generating..."
                      : downloadState === "started"
                      ? "Download Started"
                      : "Download Receipt"}
                  </span>
                </button>

                <button
                  onClick={closeModal}
                  className="w-full mt-3 bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 text-text-main dark:text-white font-bold py-3 rounded-xl transition-colors text-xs uppercase tracking-wider cursor-pointer"
                >
                  Back to catalog
                </button>
              </div>
            )}

            {checkoutStep === "applied" && (
              <div className="flex flex-col items-center justify-center py-8 text-center gap-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <FileCheck className="w-9 h-9" />
                </div>
                <h4 className="text-xl font-bold text-text-main dark:text-white">Application Received</h4>
                <p className="text-xs text-text-sub dark:text-gray-400 max-w-[300px] leading-relaxed">
                  Thank you for applying. We have received your application for <strong className="text-text-main dark:text-white">{selectedCourse?.title}</strong> and sent a confirmation email to <strong className="text-text-main dark:text-white">{email}</strong>.
                </p>
                <p className="text-xs text-text-sub dark:text-gray-400 max-w-[300px] leading-relaxed">
                  Our admissions team will review your Statement of Purpose and get back to you shortly.
                </p>
                <button
                  onClick={closeModal}
                  className="w-full mt-6 bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 text-text-main dark:text-white font-bold py-3 rounded-xl transition-colors text-xs uppercase tracking-wider cursor-pointer"
                >
                  Back to catalog
                </button>
              </div>
            )}

            {checkoutStep === "error" && (
              <div className="flex flex-col items-center justify-center py-8 text-center gap-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-500">
                  <AlertCircle className="w-9 h-9" />
                </div>
                <h4 className="text-xl font-bold text-text-main dark:text-white">Verification Failed</h4>
                <p className="text-xs text-red-500 max-w-[300px] leading-relaxed">
                  {errorMessage || "We could not verify your payment at this time. Please contact support."}
                </p>
                {isVerificationRetryable && paymentReference ? (
                  <button
                    onClick={() => verifyPayment(paymentReference, courses)}
                    className="w-full mt-6 bg-primary hover:bg-red-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-primary/20 active:scale-98 transition-all text-xs uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2"
                  >
                    <RotateCw className="w-[18px] h-[18px]" />
                    <span>Try Again</span>
                  </button>
                ) : (
                  <button
                    onClick={closeModal}
                    className="w-full mt-6 bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 text-text-main dark:text-white font-bold py-3 rounded-xl transition-colors text-xs uppercase tracking-wider cursor-pointer"
                  >
                    Back to catalog
                  </button>
                )}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesScreen;
